import Keycloak, { KeycloakProfile } from 'keycloak-js';
import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
  toJS,
} from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { logWithState } from '../functions/console.function';
import { buildFullSchemaGQL } from '../functions/request.function';
import { useStoreCommon } from '../hooks/useStore.hook';
import { Languages } from '../types/lang.type';
import { GQLContextType } from './gql.store';
import { RootStoreMobX } from './root.store';

const DEBUG = true;

type Resource = {
  name: string;
  label: string;
  description: string;
  color: string;
  title: string;
  logo: string;
  url: string;
  canUse: boolean;
};

type Me = {
  lang: Languages;
};

export type SSOContextType = {
  mode:
    | { type: 'parent' }
    | { type: 'children'; parentUrl: string }
    | { type: 'independent' };
  instance: Keycloak;
  error: 'UNCONNECTED' | 'UNAUTHORIZED' | 'INTERNAL_SERVER_ERROR' | null;
  account: KeycloakProfile;
  user: Me;
  resources: {
    modules: Array<Resource>;
    parentUrl: string;
  };
  otpState: boolean;
  exceptionPath: Array<string>;
};

const mappingError = {
  NOT_AUTHORIZED: 'UNAUTHORIZED',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  UNCONNECTED: 'UNCONNECTED',
} as const satisfies Record<string, SSOContextType['error']>;

export class SSOStoreMobX {
  rootStore: RootStoreMobX;
  @observable public mode: SSOContextType['mode'] | null = null;
  @observable public instance: SSOContextType['instance'] | null = null;
  @observable public user: SSOContextType['user'] | null = null;
  @observable public load: boolean = false;
  @observable public error: SSOContextType['error'] = null;
  @observable public account: KeycloakProfile | null = null;
  @observable public roles: Array<string> = [];
  @observable public authenticated: boolean = false;
  @observable public resources: SSOContextType['resources'] | null = null;
  @observable public otpIsActive: boolean = false;
  @observable public exceptionPath: SSOContextType['exceptionPath'] = [];
  private tokenRefreshWorker: Worker | null = null;

  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @computed get isAuthenticated(): boolean {
    return this.authenticated && this.instance?.authenticated === true;
  }

  @computed get hasUser(): boolean {
    return this.user !== null;
  }

  @computed get hasError(): boolean {
    return this.error !== null;
  }

  @computed get displayName(): string | null {
    if (!this.account) return null;
    const { firstName, lastName } = this.account;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return this.account.username || null;
  }

  @computed get hasAccess(): boolean {
    return this.isAuthenticated && this.hasUser && !this.hasError;
  }

  @computed get isAdmin(): boolean {
    return this.roles.includes('ADMIN') || this.roles.includes('admin');
  }

  @computed get availableModules(): Array<Resource> {
    return this.resources?.modules.filter((module) => module.canUse) || [];
  }

  @computed get parentUrl(): string {
    return this.resources?.parentUrl || location.origin;
  }

  @action public async auth({
    client,
    introspection,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
  }) {
    if (this.exceptionPath.includes(window.location.pathname)) {
      return;
    } else if (this.instance && !this.instance?.authenticated) {
      const checkSession = await this.checkSession();

      console.log('checkSession', checkSession);

      if (!checkSession) {
        await this.instance.login();
      }

      if (this.instance?.authenticated && this.instance.token) {
        this.setAccount(this.instance);
        this.setRoles(this.instance);
        this.setAuthenticated(this.instance);
        this.rootStore.GQLStore.updateHeaders({
          authorization: `Bearer ${this.instance.token}`,
        });
      }

      // Initialiser le Web Worker pour le refresh du token
      this.initTokenRefreshWorker();

      // Backup: Vérifier le token au retour sur l'onglet
      this.setupVisibilityListener();

      // Événement natif Keycloak quand le token expire
      this.setupKeycloakTokenExpiredListener();

      await this.setResources({ client, introspection });
      await this.setMe({ client, introspection });
      await this.getOtpState({ client, introspection });

      console.log('user', toJS(this.user));
      console.log('instance', toJS(this.instance));
      console.log('authenticated', toJS(this.authenticated));
    }
  }

  @action public async setMe({
    client,
    introspection,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
  }) {
    if (this.error) {
      return;
    }

    try {
      const { data: dataMe } = await client!.query<{
        me: Me;
      }>({
        query: buildFullSchemaGQL({
          operationName: 'me',
          operationType: 'QUERY',
          introspection,
        }),
      });

      runInAction(() => {
        this.user = dataMe.me;
      });
    } catch (e: any) {
      console.log(e);
      runInAction(() => {
        this.user = null;
        this.error =
          e?.message in mappingError
            ? mappingError[e?.message as keyof typeof mappingError]
            : 'INTERNAL_SERVER_ERROR';
      });
    }
  }

  @action public async setResources({
    client,
    introspection,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
  }) {
    if (this.error) {
      return;
    }

    try {
      const { data: dataResources } = await client!.query<{
        resources: {
          list: Array<Resource>;
        };
      }>({
        query: buildFullSchemaGQL({
          operationName: 'resources',
          operationType: 'QUERY',
          introspection,
        }),
        variables: {
          limit: 300,
        },
        fetchPolicy: 'network-only',
      });

      runInAction(() => {
        this.resources = {
          modules: dataResources.resources.list,
          parentUrl:
            this.mode?.type === 'children'
              ? this.mode.parentUrl
              : location.origin,
        };
      });
    } catch (e: any) {
      console.log(e?.message);
      runInAction(() => {
        this.resources = null;
        this.error =
          e?.message in mappingError
            ? mappingError[e?.message as keyof typeof mappingError]
            : 'INTERNAL_SERVER_ERROR';
      });
    }
  }

  @action public async checkSession(): Promise<boolean> {
    try {
      const checkSSO = await this.instance?.init({
        onLoad: 'check-sso',
        checkLoginIframe: true,
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
      });

      return !!checkSSO;
    } catch {
      return false;
    }
  }

  @action public logout() {
    // Arrêter le worker avant de se déconnecter
    if (this.tokenRefreshWorker) {
      this.tokenRefreshWorker.postMessage({ action: 'stop' });
      this.tokenRefreshWorker.terminate();
      this.tokenRefreshWorker = null;
    }
    this.instance?.logout();
  }

  /**
   * Initialise le Web Worker pour le refresh du token
   * Le worker tourne en arrière-plan et n'est pas affecté par le throttling des onglets inactifs
   */
  private initTokenRefreshWorker() {
    try {
      const workerCode = `
        let intervalId = null;

        self.addEventListener('message', (event) => {
          const { action, interval } = event.data;

          switch (action) {
            case 'start':
              if (intervalId) {
                clearInterval(intervalId);
              }

              console.log('🔧 Worker: Démarrage de l\\'intervalle de refresh (' + interval + 'ms)');

              intervalId = setInterval(() => {
                self.postMessage({
                  type: 'refresh',
                  timestamp: Date.now(),
                });
              }, interval);

              self.postMessage({
                type: 'started',
                interval,
              });
              break;

            case 'stop':
              if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
                console.log('🔧 Worker: Arrêt de l\\'intervalle de refresh');
              }

              self.postMessage({
                type: 'stopped',
              });
              break;

            case 'ping':
              self.postMessage({
                type: 'pong',
                timestamp: Date.now(),
              });
              break;

            default:
              console.warn('🔧 Worker: Action inconnue: ' + action);
          }
        });

        console.log('🔧 Worker: Token Refresh Worker initialisé');
      `;

      // Créer le worker à partir du code inline
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      this.tokenRefreshWorker = new Worker(workerUrl);

      // Écouter les messages du worker
      this.tokenRefreshWorker.addEventListener('message', (event) => {
        const { type } = event.data;

        if (type === 'refresh') {
          // Le worker demande un refresh du token
          this.refreshToken();
        } else if (type === 'started') {
          console.log('✅ Worker de refresh du token démarré');
        }
      });

      // Gérer les erreurs du worker
      this.tokenRefreshWorker.addEventListener('error', (error) => {
        console.error('❌ Erreur dans le worker de refresh:', error);
      });

      // Démarrer le worker avec un intervalle de 20 secondes
      this.tokenRefreshWorker.postMessage({
        action: 'start',
        interval: 20000,
      });
    } catch (error) {
      console.error(
        '❌ Impossible de créer le worker, fallback sur setInterval:',
        error,
      );
      // Fallback sur setInterval classique si le worker ne peut pas être créé
      this.fallbackToSetInterval();
    }
  }

  /**
   * Méthode de refresh du token appelée par le worker
   */
  private async refreshToken() {
    try {
      // Logs des informations de session avant le refresh
      if (this.instance) {
        const now = Math.floor(Date.now() / 1000);
        const tokenExpiration = this.instance.tokenParsed?.exp || 0;
        const refreshTokenExpiration =
          this.instance.refreshTokenParsed?.exp || 0;
        const sessionState = this.instance.sessionId;

        const tokenTimeRemaining = tokenExpiration - now;
        const refreshTokenTimeRemaining = refreshTokenExpiration - now;

        if (DEBUG) {
          console.log('📊 État de la session Keycloak:', {
            sessionId: sessionState,
            authenticated: this.instance.authenticated,
            tokenExpiration: {
              timestamp: tokenExpiration,
              date: new Date(tokenExpiration * 1000).toLocaleString('fr-FR'),
              tempsRestant: `${Math.floor(tokenTimeRemaining / 60)}m ${tokenTimeRemaining % 60}s`,
              tempsRestantSecondes: tokenTimeRemaining,
              expired: tokenTimeRemaining <= 0,
            },
            refreshToken: {
              timestamp: refreshTokenExpiration,
              date: new Date(refreshTokenExpiration * 1000).toLocaleString(
                'fr-FR',
              ),
              tempsRestant: `${Math.floor(refreshTokenTimeRemaining / 60)}m ${refreshTokenTimeRemaining % 60}s`,
              tempsRestantSecondes: refreshTokenTimeRemaining,
              expired: refreshTokenTimeRemaining <= 0,
            },
            timeSkew: this.instance.timeSkew,
          });
        }
      }

      const refreshed = await this.instance?.updateToken(100);

      runInAction(() => {
        if (refreshed) {
          console.log('✅ Token rafraîchi avec succès');

          if (this.instance) {
            const now = Math.floor(Date.now() / 1000);
            const newTokenExpiration = this.instance.tokenParsed?.exp || 0;
            const newTokenTimeRemaining = newTokenExpiration - now;

            if (DEBUG) {
              console.log('🔄 Nouveau token:', {
                expiration: new Date(newTokenExpiration * 1000).toLocaleString(
                  'fr-FR',
                ),
                tempsRestant: `${Math.floor(newTokenTimeRemaining / 60)}m ${newTokenTimeRemaining % 60}s`,
                tempsRestantSecondes: newTokenTimeRemaining,
              });
            }
          }
        } else {
          if (DEBUG) {
            console.log(
              'ℹ️ Token pas encore expiré, pas de rafraîchissement nécessaire',
            );
          }
        }

        if (this.instance?.token) {
          this.rootStore.GQLStore.updateHeaders({
            authorization: `Bearer ${this.instance.token}`,
          });
        }
      });
    } catch (error) {
      runInAction(() => {
        console.error('❌ Erreur lors du rafraîchissement du token:', {
          error,
          message: error instanceof Error ? error.message : 'Erreur inconnue',
          sessionExpired: this.instance?.refreshTokenParsed?.exp
            ? Math.floor(Date.now() / 1000) >
              this.instance.refreshTokenParsed.exp
            : 'inconnu',
        });
        this.logout();
      });
    }
  }

  /**
   * Fallback sur setInterval si le worker ne peut pas être créé
   */
  private fallbackToSetInterval() {
    setInterval(() => {
      this.refreshToken();
    }, 20000);
  }

  /**
   * Configure le listener de visibilité pour vérifier le token au retour sur l'onglet
   */
  private setupVisibilityListener() {
    document.addEventListener('visibilitychange', async () => {
      if (!document.hidden && this.instance?.authenticated) {
        console.log("👁️ Retour sur l'onglet - vérification du token");
        try {
          const refreshed = await this.instance?.updateToken(5);

          runInAction(() => {
            if (refreshed && this.instance?.token) {
              this.rootStore.GQLStore.updateHeaders({
                authorization: `Bearer ${this.instance.token}`,
              });
              console.log("✅ Token vérifié/rafraîchi au retour sur l'onglet");
            }
          });
        } catch (error) {
          console.error('❌ Erreur lors de la vérification au retour:', error);
          runInAction(() => {
            this.logout();
          });
        }
      }
    });
  }

  /**
   * Configure le listener natif Keycloak pour l'expiration du token
   */
  private setupKeycloakTokenExpiredListener() {
    if (this.instance) {
      this.instance.onTokenExpired = async () => {
        console.log(
          '⏰ Token expiré (événement Keycloak) - rafraîchissement automatique',
        );
        try {
          const refreshed = await this.instance?.updateToken(30);

          runInAction(() => {
            if (refreshed && this.instance?.token) {
              this.rootStore.GQLStore.updateHeaders({
                authorization: `Bearer ${this.instance.token}`,
              });
              console.log("✅ Token rafraîchi suite à l'expiration");
            }
          });
        } catch (error) {
          console.error('❌ Impossible de rafraîchir le token expiré:', error);
          runInAction(() => {
            this.logout();
          });
        }
      };
    }
  }

  @action public async getOtpState({
    client,
    introspection,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
  }) {
    try {
      const { data } = await client!.query<{ otp: { isActive: boolean } }>({
        query: buildFullSchemaGQL({
          operationName: 'otp',
          operationType: 'QUERY',
          introspection,
        }),
      });

      runInAction(() => {
        this.otpIsActive = data.otp.isActive;
      });
    } catch (e: any) {
      runInAction(() => {
        this.error =
          e?.message in mappingError
            ? mappingError[e?.message as keyof typeof mappingError]
            : 'INTERNAL_SERVER_ERROR';
      });
    }
  }

  @action public async activeOtp({
    client,
    introspection,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
  }): Promise<boolean> {
    try {
      const { data } = await client!.mutate<
        any,
        {
          activeOtp: { success: boolean };
        }
      >({
        mutation: buildFullSchemaGQL({
          operationName: 'activeOtp',
          operationType: 'MUTATION',
          introspection,
        }),
      });

      runInAction(() => {
        this.otpIsActive = true;
      });

      window.location.reload();
      return data.activeOtp.success;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  @action public async inactiveOtp({
    client,
    introspection,
  }: {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
  }): Promise<boolean> {
    try {
      const { data } = await client!.mutate<
        any,
        {
          inactiveOtp: { success: boolean };
        }
      >({
        mutation: buildFullSchemaGQL({
          operationName: 'inactiveOtp',
          operationType: 'MUTATION',
          introspection,
        }),
      });

      runInAction(() => {
        this.otpIsActive = false;
      });

      return data.inactiveOtp.success;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  @observable private setRoles(instance: SSOContextType['instance']) {
    this.roles =
      instance?.resourceAccess &&
      instance?.clientId &&
      instance?.clientId in instance.resourceAccess
        ? Object.values(instance.resourceAccess?.[instance?.clientId]?.roles)
        : [];
  }

  @observable public setAuthenticated(instance: SSOContextType['instance']) {
    this.authenticated = !!instance?.authenticated;
  }

  @action private async setAccount(instance: SSOContextType['instance']) {
    const account = (await instance?.loadUserProfile()) || null;

    this.account = {
      ...account,
      avatar: account?.attributes?.avatar?.[0] || null,
    };
    return account;
  }

  @action public async init({
    mode,
    instance,
    client,
    introspection,
    exceptionPath,
  }: Pick<SSOContextType, 'mode' | 'instance'> & {
    client: GQLContextType['client'];
    introspection: GQLContextType['introspection'];
    exceptionPath?: SSOContextType['exceptionPath'];
  }) {
    runInAction(() => {
      this.load = false;
      this.mode = mode;
      this.instance = new Keycloak({
        url: instance.url,
        realm: instance.realm,
        clientId: instance.clientId,
      });
      this.exceptionPath = exceptionPath || [];
    });

    await this.auth({ client, introspection });

    runInAction(() => {
      this.load = true;
    });
  }
}

export const ConfigSSO = observer(
  ({
    children,
    mode,
    instance,
    exceptionPath,
  }: { children: React.ReactNode } & Pick<SSOContextType, 'mode'> &
    Partial<Pick<SSOContextType, 'exceptionPath'>> & {
      instance: { url: string; realm: string; clientId: string };
    }) => {
    //! Dépend du GQLStore
    const { GQLStore, SSOStore } = useStoreCommon();

    useEffect(() => {
      (async () => {
        if (
          !SSOStore.load &&
          GQLStore.load &&
          instance &&
          mode &&
          GQLStore.client &&
          GQLStore.introspection
        ) {
          logWithState({ state: 'INFO', value: 'ConfigSSO init' });

          await SSOStore.init({
            instance,
            mode,
            client: GQLStore.client!,
            introspection: GQLStore.introspection!,
            exceptionPath: exceptionPath || [],
          });
        }

        if (SSOStore.load) {
          logWithState({ state: 'INFO', value: 'ConfigSSO load' });
        }
      })();
    }, [
      SSOStore.load,
      instance,
      mode,
      GQLStore.load,
      GQLStore.client,
      GQLStore.introspection,
    ]);

    return SSOStore.load ? children : null;
  },
);
